import React, { useState, useEffect } from "react";
import NavBar from "../components/NavBar";
import {
  Box,
  Flex,
  SimpleGrid,
  Heading,
  Image,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import ambassidor from "../images/ojas.jpg";
import PrevInterns from "../components/PrevInterns";
import GlobalNetwork from "../components/GlobalNetwork";
import Footer from "../components/Footer";
import vedika1 from "../images/vedika1.png";
import vedika2 from "../images/vedika2.png";
import vedika3 from "../images/vedika3.png";
import ojas1 from "../images/ojas1.png";
import ojas2 from "../images/ojas2.png";
import { useSelector } from "react-redux";

// ✅ New hero imports
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Sparkles, GraduationCap, Globe, Handshake } from "lucide-react";
import aboutBg from "../images/about-hero-bg.jpg";

/* ================= HERO SECTION (UNCHANGED) ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const orbitItems = [
  { icon: Rocket, label: "Innovation", angle: 0 },
  { icon: GraduationCap, label: "Mentorship", angle: 72 },
  { icon: Globe, label: "Community", angle: 144 },
  { icon: Handshake, label: "Growth", angle: 216 },
  { icon: Sparkles, label: "Creativity", angle: 288 },
];

const AboutHero = () => {
  const radius = 165;
  const center = 210;

  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2A6F] via-[#0F3C96] to-[#0F3C96]" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-overlay"
          style={{ backgroundImage: `url(${aboutBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-green-400/20 via-transparent to-green-300/10" />
        <div className="absolute inset-0 bg-black/35" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <motion.div
            className="flex-1 text-center lg:text-left text-white"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1
              variants={fadeUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight mb-6"
            >
              About{" "}
              <span className="bg-gradient-to-r from-green-300 to-green-500 bg-clip-text text-transparent ">
                Us
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-xl sm:text-2xl text-white/90 leading-relaxed max-w-2xl mx-auto lg:mx-0"
            >
              We believe in fostering the{" "}
              <span className="font-bold text-green-300">
                next generation of talent
              </span>
              . Our internship program is more than just an opportunity — it’s a
              gateway to{" "}
              <span className="font-semibold text-green-200">
                hands-on experience
              </span>
              , mentorship, and a vibrant community that values{" "}
              <span className="font-semibold text-green-300">
                innovation, growth, and diversity.
              </span>
            </motion.p>
          </motion.div>

          <motion.div
            className="flex-1 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative w-[420px] h-[420px]">
              <motion.div
                className="absolute inset-4 rounded-full border border-green-300/30 shadow-[0_0_60px_rgba(34,197,94,0.25)]"
                animate={{ rotate: 360 }}
                transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
              />

              <motion.div
                className="absolute inset-14 rounded-full border border-white/25 border-dashed"
                animate={{ rotate: -360 }}
                transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
              />

              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="w-28 h-28 rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white font-extrabold text-xl flex items-center justify-center shadow-2xl"
                  animate={{ scale: [1, 1.12, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  GYAN
                </motion.div>
              </div>

              {orbitItems.map((item, i) => {
                const angle = (item.angle * Math.PI) / 180;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;
                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.label}
                    className="absolute"
                    style={{ left: center + x - 30, top: center + y - 30 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + i * 0.12 }}
                  >
                    <div className="flex flex-col items-center gap-1 text-white">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/20 to-green-300/20 backdrop-blur-lg border border-white/40 flex items-center justify-center shadow-xl hover:scale-110 transition">
                        <Icon className="w-7 h-7 text-green-300" />
                      </div>
                      <span className="text-xs font-semibold">{item.label}</span>
                    </div>
                  </motion.div>
                );
              })}

              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                {orbitItems.map((item, i) => {
                  const angle = (item.angle * Math.PI) / 180;
                  const x = center + Math.cos(angle) * radius;
                  const y = center + Math.sin(angle) * radius;
                  return (
                    <line
                      key={i}
                      x1={center}
                      y1={center}
                      x2={x}
                      y2={y}
                      stroke="rgba(34,197,94,0.25)"
                      strokeWidth="1"
                    />
                  );
                })}
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ================= INSPIRATION CARD COMPONENT ================= */

const InspirationCard = ({ person, index, onSelect }) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    if (!person.images || person.images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentImgIndex((prev) => (prev + 1) % person.images.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [person.images]);

  // Support either an array of titles or a single fallback title string
  const currentTitle = Array.isArray(person.titles)
    ? person.titles[currentImgIndex]
    : person.title;

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ duration: 0.7, delay: index * 0.2 }}
      style={{ width: "100%", height: "100%" }}
    >
      <Box
        bg="whiteAlpha.150"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255,255,255,0.35)"
        borderRadius="3xl"
        p={{ base: 6, sm: 8, md: 7, lg: 9 }}
        boxShadow="0 25px 80px rgba(0,0,0,0.45)"
        position="relative"
        overflow="hidden"
        h="100%"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          {/* Flip / Swap Image Frame with 3D Perspective */}
          <Box
            position="relative"
            w={{ base: "240px", sm: "270px", md: "280px", lg: "320px" }}
            h={{ base: "270px", sm: "300px", md: "310px", lg: "350px" }}
            mx="auto"
            mb={8}
            style={{ perspective: 1200 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentImgIndex}
                initial={{ rotateY: 90, opacity: 0, scale: 0.94 }}
                animate={{ rotateY: 0, opacity: 1, scale: 1 }}
                exit={{ rotateY: -90, opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.75, ease: [0.4, 0, 0.2, 1] }}
                style={{
                  width: "100%",
                  height: "100%",
                  transformStyle: "preserve-3d",
                  position: "relative",
                }}
              >
                <Box
                  w="100%"
                  h="100%"
                  borderRadius="2xl"
                  overflow="hidden"
                  border="4px solid rgba(134,239,172,0.85)"
                  boxShadow="0 20px 40px rgba(0,0,0,0.5), 0 0 25px rgba(74,222,128,0.25)"
                  position="relative"
                  bg="gray.950"
                >
                  {/* Ambient blurred backdrop for seamless framing */}
                  <Image
                    src={person.images[currentImgIndex]}
                    alt=""
                    position="absolute"
                    inset="-12px"
                    w="calc(100% + 24px)"
                    h="calc(100% + 24px)"
                    objectFit="cover"
                    filter="blur(18px) brightness(0.4)"
                    transform="scale(1.15)"
                  />

                  {/* Foreground high-res crisp image */}
                  <Image
                    src={person.images[currentImgIndex]}
                    alt={person.name}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                    position="relative"
                    zIndex={1}
                  />

                  {/* Vignette overlay */}
                  <Box
                    position="absolute"
                    inset={0}
                    zIndex={2}
                    bgGradient="linear(to-t, rgba(0,0,0,0.6) 0%, transparent 40%, rgba(0,0,0,0.2) 100%)"
                    pointerEvents="none"
                  />

                  {/* Image Badge for multiple images */}
                  {person.images.length > 1 && (
                    <Box
                      position="absolute"
                      top={3}
                      right={3}
                      zIndex={3}
                      bg="blackAlpha.750"
                      backdropFilter="blur(8px)"
                      border="1px solid rgba(255,255,255,0.25)"
                      color="green.300"
                      fontSize="xs"
                      fontWeight="bold"
                      px={2.5}
                      py={1}
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                      {currentImgIndex + 1} / {person.images.length}
                    </Box>
                  )}
                </Box>
              </motion.div>
            </AnimatePresence>

            {/* Interactive Dot Indicators for swapping/flipping */}
            {person.images.length > 1 && (
              <Flex
                position="absolute"
                bottom="-22px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={4}
                gap={2}
                align="center"
              >
                {person.images.map((_, dotIdx) => (
                  <Box
                    key={dotIdx}
                    as="button"
                    onClick={() => setCurrentImgIndex(dotIdx)}
                    w={dotIdx === currentImgIndex ? "24px" : "8px"}
                    h="8px"
                    borderRadius="full"
                    bg={dotIdx === currentImgIndex ? "green.400" : "whiteAlpha.400"}
                    boxShadow={dotIdx === currentImgIndex ? "0 0 10px rgba(74,222,128,0.8)" : "none"}
                    transition="all 0.3s ease"
                    _hover={{ bg: "green.300" }}
                    aria-label={`Show image ${dotIdx + 1}`}
                  />
                ))}
              </Flex>
            )}
          </Box>

          <Text color="green.200" fontWeight="bold" letterSpacing="widest" fontSize="sm" mb={2} textTransform="uppercase">
            {currentTitle}
          </Text>

          <Heading fontSize={{ base: "2xl", md: "3xl" }} color="white" mb={5} fontFamily="Playfair Display">
            {person.name}
          </Heading>
        </Box>

        <Button
          size="lg"
          w="fit-content"
          mx="auto"
          mt={3}
          bgGradient="linear(to-r, green.400, green.600)"
          color="white"
          _hover={{ bgGradient: "linear(to-r, green.500, green.700)", transform: "translateY(-2px)" }}
          onClick={() => onSelect({
            ...person,
            currentImg: person.images[currentImgIndex],
            currentTitle: currentTitle
          })}
        >
          Read More
        </Button>
      </Box>
    </motion.div>
  );
};

/* ================= MAIN PAGE ================= */

const AboutUs = () => {
  const { isAdmin } = useSelector((state) => state.user);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const inspirations = [
    {
      images: [ambassidor, ojas1, ojas2],
      titles: [
        "Arjun Award Winner | Archery",
        "World Archery Champion",
        "International Gold Medalist"
      ],
      name: "Ojas Pravin Deotale",
      desc: (
        <>
          Ojas Pravin Deotale is an Indian archer currently ranked 9th in the world.
          He made history by becoming the first Indian male archer to win gold in the men's individual compound event at the
          <strong> 2023 World Archery Championships</strong> in Berlin.
        </>
      ),
    },
    {
      images: [vedika1, vedika2, vedika3],
      titles: [
        "Captain, U-16 Indian Korfball Team",
        "National Korfball Prodigy",
        "International Korfball runner-up"
      ],
      name: "Vedika Kalbande",
      desc: (
        <>
          Vedika Kalbande is an exceptional sports prodigy and the <strong>Captain of the U-16 Indian Korfball Team</strong>.
          Representing India at national and international championships under the International Korfball Federation (IKF Asia), she has earned high honors including <strong>Player of the Match</strong> and championship trophies.
          With her fierce determination, stellar athleticism, and natural leadership, she continues to inspire young athletes nationwide.
          We proudly support Vedika and look forward to witnessing her incredible journey ahead.
        </>
      ),
    },
  ];

  const MotionBox = motion(Box);

  return (
    <div style={{ fontFamily: "Poppins, sans-serif", marginTop: 0, paddingTop: 0 }}>
      <NavBar isAdmin={isAdmin} />

      <AboutHero />

      <GlobalNetwork />
      <PrevInterns />

      {/* ===== THEMED INSPIRATIONS SECTION ===== */}
      <Box position="relative" py={24} overflow="hidden">
        <Box position="absolute" inset={0} bgGradient="linear(to-b, #064e3b, #065f46, #022c22)" />

        {/* dot pattern */}
        <Box
          position="absolute"
          inset={0}
          opacity={0.12}
          backgroundImage="radial-gradient(#a7f3d0 1px, transparent 1px)"
          backgroundSize="22px 22px"
        />

        <Box position="absolute" top="-80px" left="-80px" w="300px" h="300px" bg="green.400" opacity={0.25} filter="blur(120px)" />
        <Box position="absolute" bottom="-80px" right="-80px" w="300px" h="300px" bg="green.300" opacity={0.2} filter="blur(120px)" />

        <Box position="relative" zIndex={1} px={{ base: 4, sm: 6, md: 8 }} textAlign="center" maxW="1280px" mx="auto">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Text letterSpacing="0.3em" textTransform="uppercase" color="green.200" fontSize="sm" mb={4}>
              Who Inspires Us
            </Text>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight text-white"
            >
              Our <span className="bg-gradient-to-r from-green-300 to-green-500 bg-clip-text text-transparent">Inspirations</span>
            </motion.h2>

            {/* ✅ animated subtext — only here */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-6 text-lg text-white max-w-3xl mx-auto"
            >
              At Gyan, we believe that true greatness is born from dedication, resilience, and the courage to dream beyond boundaries. These remarkable individuals embody that spirit.
            </motion.p>
          </motion.div>

          <MotionBox
            mt={6}   // adds space from top

            w="120px"
            h="3px"
            bgGradient="linear(to-r, #48BB78, #3182CE)"
            borderRadius="full"
            mx="auto"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />

          {/* Side by side cards grid */}
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 8, md: 6, lg: 8 }}
            maxW="1180px"
            mx="auto"
            mt={16}
            w="100%"
            alignItems="stretch"
          >
            {inspirations.map((person, index) => (
              <InspirationCard
                key={index}
                person={person}
                index={index}
                onSelect={(selected) => {
                  setSelectedPerson(selected);
                  onOpen();
                }}
              />
            ))}
          </SimpleGrid>
        </Box>

        <Modal isOpen={isOpen} onClose={() => { setSelectedPerson(null); onClose(); }} isCentered size="xl">
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent bg="white" color="gray.800" borderRadius="2xl" overflow="hidden">
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text fontSize="xl" fontWeight="bold">{selectedPerson?.name}</Text>
              <Text fontSize="sm" color="green.600" fontWeight="medium">{selectedPerson?.currentTitle}</Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6}>
              {selectedPerson?.currentImg && (
                <Box
                  w="100%"
                  maxH="320px"
                  mb={6}
                  borderRadius="xl"
                  overflow="hidden"
                  bg="gray.900"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Image
                    src={selectedPerson.currentImg}
                    alt={selectedPerson.name}
                    maxH="320px"
                    w="auto"
                    objectFit="contain"
                  />
                </Box>
              )}
              <Text color="gray.700" fontSize="md" lineHeight="tall">{selectedPerson?.desc}</Text>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100">
              <Button bg="green.600" color="white" _hover={{ bg: "green.700" }} onClick={onClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>

      <Footer />
    </div>
  );
};

export default AboutUs;